/**
 * AssetKbFiles — the IVT Detail "Knowledge Base" tab (asset + UC detail, owner-generic).
 * File management (real ops): UPLOAD (presigned two-step) + LIST (via GetKbTree) +
 * DOWNLOAD + DELETE + in-place txt/md EDIT. Allowed formats: pdf / doc / docx / pptx /
 * xls / xlsx / csv / html / txt / md (all Managed-KB-native), ≤ 500MB. Each file shows
 * an ingest-status badge (处理中 / 已就绪 / 处理失败) from KbEntry.status.
 *
 * Data source: the owner-generic useKb (GetKbTree). The tree is folders[] + root
 * entries[] (+ overview, not rendered this round). Until the backend seeds folders,
 * folders[] is empty and everything is root-level → a flat list (progressive reveal).
 *
 * Layout:
 *   - header: "▶ Knowledge Base"  "N sources"
 *   - LEFT rail: "Sources" + Refresh + Upload; folders (collapsible, with entry count)
 *     then root entries; one row per entry (name + status badge).
 *   - RIGHT pane: the selected entry. txt/md render inline (md via MarkdownRenderer)
 *     and can be EDITED in place. Non-previewable shows file info + Download.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, FormField, Icon, Input, Modal, SpaceBetween, Spinner, StatusIndicator, Textarea,
} from '@amzn/awsui-components-react';
import { KbFileStatus, type KbEntry } from '../../../client/IvtHubClient';
import { KB_UPLOAD_ACCEPT, isEditableFile } from '../hooks/kbFormats';
import { useKb, type UseKb } from '../hooks/useKb';
import MarkdownRenderer from '../../../../components/ChatPanel/MarkdownRenderer';
import './AssetKbFiles.css';

const TEXT_EXTS = ['.txt', '.md'];

function isTextFile(name?: string): boolean {
  const n = (name ?? '').toLowerCase();
  return TEXT_EXTS.some((ext) => n.endsWith(ext));
}
/** A URL entry (a link, not a file). Branch on entryType — NOT status: a url entry's
 *  status is the sentinel 'saved' (outside KbFileStatus), so status-based logic misfires. */
function isUrlEntry(e?: KbEntry): boolean {
  return e?.entryType === 'url';
}
/** Only render a url as a clickable <a> if its scheme is http/https — a second guard
 *  independent of the backend's scheme check (defence in depth; a non-http(s) value
 *  renders as plain text, never a link). Parsed via URL() so we never string-match. */
function isHttpUrl(url?: string): boolean {
  if (!url) return false;
  try {
    const proto = new URL(url).protocol;
    return proto === 'http:' || proto === 'https:';
  } catch {
    return false;
  }
}
function formatSize(bytes?: number): string {
  if (bytes == null) return '';
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

// KB ingest status → a Cloudscape StatusIndicator (type + label + optional hint).
type KbStatusView = { type: 'in-progress' | 'success' | 'error' | 'pending'; label: string; hint?: string };
function statusView(status?: string): KbStatusView {
  switch (status) {
    case KbFileStatus.READY:
      return { type: 'success', label: '知识库已就绪' };
    case KbFileStatus.FAILED:
      return {
        type: 'error',
        label: '处理失败',
        hint: '知识库无法解析该文件的内容（可能是无法提取文本、内容为空或格式异常）。请删除后重新上传，或换一个文件。',
      };
    case KbFileStatus.UPLOADED:
      return { type: 'in-progress', label: '知识库处理中' };
    default:
      return { type: 'pending', label: '等待中' };
  }
}

/**
 * KbFilesPanel — the owner-agnostic KB panel. Takes a useKb RESULT so the SAME panel
 * serves the Asset detail KB tab and the UC detail KB tab (and, later, Storyline). All
 * owner keying lives in useKb / kbWriteOps, never here.
 */
export function KbFilesPanel({ kb }: { kb: UseKb }) {
  const {
    rootEntries, folders, entryCount, loading, error, reload,
    upload, uploading, uploadError,
    remove, deletingId, deleteError,
    download, downloadingId, downloadError,
    fetchTextContent,
    saveEdit, savingEditId, editError,
    overview, saveOverview, savingOverview, overviewError,
    addUrl, updateUrl, savingUrl, urlError, clearUrlError,
  } = kb;
  const inputRef = useRef<HTMLInputElement>(null);
  // The entry pending delete-confirmation (null = dialog closed).
  const [confirmDelete, setConfirmDelete] = useState<KbEntry | null>(null);
  // In-place edit of the selected txt/md file: null = viewing, string = editing (draft).
  const [draft, setDraft] = useState<string | null>(null);
  // Inline text preview (txt/md) of the selected entry: loading / content / error.
  const [preview, setPreview] = useState<{ loading: boolean; text: string | null; error: string | null }>({
    loading: false,
    text: null,
    error: null,
  });
  // Selected FILE entry id (fileId), or null = nothing selected. Overview selection is
  // tracked separately (see overviewSelected) — NOT via this id — because the Overview's
  // id flips from a placeholder sentinel to a real fileId on first save; matching on id
  // would deselect it right after the user saves.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Whether the (pinned) Overview row is the current selection. A dedicated flag so it
  // survives the placeholder→real-id transition on first save.
  const [overviewSelected, setOverviewSelected] = useState(false);
  // The selected FOLDER, if any (folded tree: a folder is a valid selection — it becomes
  // the Upload target — even though its files stay nested in the rail rather than the
  // right pane). null = no folder selected. Mutually exclusive with selectedId/overview.
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  // Collapsed folder ids (folders default expanded).
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  // URL add/edit inline form. null = closed. { folderId } = ADD into that folder (or root
  // if undefined); { fileId } = EDIT that existing url entry. Draft holds the field values.
  const [urlForm, setUrlForm] = useState<
    | { mode: 'add'; folderId?: string; displayName: string; url: string }
    | { mode: 'edit'; fileId: string; displayName: string; url: string }
    | null
  >(null);

  const overviewSaved = !!overview;
  const isOverviewSelected = overviewSelected;

  // Three mutually-exclusive selections: Overview, a file, or a folder. Each selector
  // clears the other two.
  const selectOverview = () => {
    setOverviewSelected(true);
    setSelectedId(null);
    setSelectedFolderId(null);
  };
  const selectFile = (fileId: string) => {
    setOverviewSelected(false);
    setSelectedId(fileId);
    setSelectedFolderId(null);
  };
  // Select a folder (= make it the Upload target) and ensure it's expanded so the drop
  // location is visible.
  const selectFolder = (folderId: string) => {
    setOverviewSelected(false);
    setSelectedId(null);
    setSelectedFolderId(folderId);
    setCollapsed((prev) => {
      if (!prev.has(folderId)) return prev;
      const next = new Set(prev);
      next.delete(folderId);
      return next;
    });
  };

  // All entries flattened (root + every folder) — for selection lookup + total count.
  // Overview is NOT included (it has its own pinned row + save path).
  const allEntries = useMemo(
    () => [...folders.flatMap((f) => f.entries), ...rootEntries],
    [folders, rootEntries],
  );

  // The upload TARGET is decided by WHICH Upload button was clicked, not by the current
  // selection: the "Sources" row's Upload targets root; a selected folder's pane Upload
  // targets that folder. We share one hidden <input>, so the button records its intended
  // folder here (undefined = root) right before opening the picker; handleSelected reads it.
  const uploadTargetRef = useRef<string | undefined>(undefined);
  const openUpload = (folderId?: string) => {
    uploadTargetRef.current = folderId;
    inputRef.current?.click();
  };

  async function handleSelected(list: FileList | null) {
    if (!list || list.length === 0) return;
    const folderId = uploadTargetRef.current; // captured at click time
    await upload(Array.from(list), folderId); // batch: one reload, errors accumulated
    if (inputRef.current) inputRef.current.value = ''; // allow re-selecting the same file
  }

  // The selected folder's model (for its right-pane header), if a folder is selected.
  const selectedFolder = useMemo(
    () => (selectedFolderId ? folders.find((f) => f.folder.folderId === selectedFolderId) : undefined),
    [selectedFolderId, folders],
  );

  async function handleConfirmDelete() {
    if (!confirmDelete?.fileId) return;
    const ok = await remove(confirmDelete.fileId);
    if (ok) {
      if (selectedId === confirmDelete.fileId) setSelectedId(null);
      setConfirmDelete(null);
    }
  }

  // The selected entry — including the Overview when its id (real or placeholder) is
  // picked. The placeholder overview has no content to fetch (absent = never saved).
  const selectedEntry: KbEntry | undefined = isOverviewSelected
    ? overview
    : selectedId === null
      ? undefined
      : allEntries.find((e) => e.fileId === selectedId);
  const selectedName = selectedEntry?.displayName;

  // When a text entry is selected, fetch its content for inline preview. Overview is
  // markdown → fetch it too (only when saved; the placeholder has nothing). Depend on
  // STABLE primitives (selectedId + selectedName), NOT the entry object — `folders`/
  // `rootEntries` get new refs on every reload, so keying on the object would re-fetch
  // on every unrelated mutation.
  useEffect(() => {
    // A URL entry is NEVER previewable (it has no S3 object) — even if its free-text
    // displayName happens to end in .md/.txt. Guard on entryType so a link named
    // "notes.md" doesn't trigger a doomed GetKbFileDownloadUrl fetch.
    const previewable = selectedEntry && !isUrlEntry(selectedEntry) && (isTextFile(selectedName) || isOverviewSelected);
    if (!previewable) {
      setPreview({ loading: false, text: null, error: null });
      return;
    }
    let cancelled = false;
    setPreview({ loading: true, text: null, error: null });
    fetchTextContent(selectedEntry)
      .then((text) => {
        if (!cancelled) setPreview({ loading: false, text, error: null });
      })
      .catch((e) => {
        if (!cancelled) setPreview({ loading: false, text: null, error: (e as Error).message ?? '预览失败' });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, selectedName, overviewSelected, fetchTextContent]);

  // Leaving an entry (selection change, file OR overview) drops any unsaved edit draft.
  useEffect(() => {
    setDraft(null);
  }, [selectedId, overviewSelected]);
  // Leaving the current entry/folder also closes any open URL form (its draft is scoped
  // to that selection) and clears any prior URL error, so a failed save's message can't
  // leak onto the next selection's form.
  useEffect(() => {
    setUrlForm(null);
    clearUrlError();
  }, [selectedId, overviewSelected, selectedFolderId, clearUrlError]);

  function startEdit() {
    setDraft(preview.text ?? '');
  }

  // Save the inline URL form (add → addUrl into its folder; edit → updateUrl the entry).
  // Trim the display name; the URL is sent as-is (backend validates scheme). Closes on ok.
  async function handleSaveUrl() {
    if (!urlForm) return;
    const displayName = urlForm.displayName.trim();
    const url = urlForm.url.trim();
    const ok =
      urlForm.mode === 'add'
        ? await addUrl(displayName, url, urlForm.folderId)
        : await updateUrl(urlForm.fileId, { displayName, url });
    if (ok) setUrlForm(null);
  }
  // The URL form is only saveable with a non-empty display name AND a non-empty URL.
  const urlFormValid = !!urlForm && urlForm.displayName.trim().length > 0 && urlForm.url.trim().length > 0;
  async function handleSaveEdit() {
    if (draft === null) return;
    // Overview routes to saveOverview (first save = upload flow, else edit flow); a
    // regular file routes to saveEdit. Overview may be a placeholder (no selectedEntry).
    const ok = isOverviewSelected ? await saveOverview(draft) : selectedEntry ? await saveEdit(selectedEntry, draft) : false;
    if (ok) {
      setPreview({ loading: false, text: draft, error: null });
      setDraft(null);
    }
  }

  const editing = draft !== null;
  // Overview is always editable (markdown); a regular file only if txt/md.
  const canEditSelected = isOverviewSelected || (!!selectedEntry && isEditableFile(selectedEntry.displayName));
  // Whether the current selection is a saved Overview vs the not-yet-saved placeholder.
  const overviewPlaceholderSelected = isOverviewSelected && !overviewSaved;

  // One entry row in the left rail (shared by folders + root). A URL entry has no ingest
  // status (it's a link, not ingested) — show a link icon instead of the status badge;
  // branch on entryType, never on status (a url's status is the sentinel 'saved').
  const entryRow = (e: KbEntry) => {
    const isUrl = isUrlEntry(e);
    const sv = isUrl ? null : statusView(e.status);
    return (
      <button
        key={e.fileId}
        type="button"
        className={`ivt-kb__entry${selectedId === e.fileId ? ' ivt-kb__entry--active' : ''}`}
        onClick={() => e.fileId && selectFile(e.fileId)}
        title={e.displayName ?? undefined}
      >
        <span className="ivt-kb__entry-name">
          {isUrl && <Icon name="external" size="small" />} {e.displayName ?? '—'}
        </span>
        {sv && (
          <span className="ivt-kb__entry-status">
            <StatusIndicator type={sv.type}>{sv.label}</StatusIndicator>
          </span>
        )}
        {sv?.hint && <span className="ivt-kb__entry-hint">{sv.hint}</span>}
      </button>
    );
  };

  const toggleFolder = (folderId: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });

  // The inline URL add/edit form — a file-view head (title + Cancel/Save) over two
  // fields (Display Name + URL). Shared by folder-pane "Add URL" and url-entry "Edit".
  const urlFormView = (title: string) =>
    urlForm && (
      <div className="ivt-kb__file-view">
        <div className="ivt-kb__file-head">
          <span className="ivt-kb__file-name">{title}</span>
          <span className="ivt-kb__file-actions">
            <Button variant="primary" loading={savingUrl} disabled={!urlFormValid} onClick={() => void handleSaveUrl()}>
              保存
            </Button>
            <Button variant="link" disabled={savingUrl} onClick={() => { setUrlForm(null); clearUrlError(); }}>
              取消
            </Button>
          </span>
        </div>
        <div className="ivt-kb__url-form">
          <SpaceBetween size="l">
            {urlError && <Alert type="error">{urlError}</Alert>}
            <FormField label="Display Name" stretch>
              <Input
                value={urlForm.displayName}
                onChange={({ detail }) => setUrlForm({ ...urlForm, displayName: detail.value })}
                placeholder="例如：AWS Blog"
                disabled={savingUrl}
              />
            </FormField>
            <FormField label="URL" description="仅支持 http / https 链接。" stretch>
              <Input
                value={urlForm.url}
                onChange={({ detail }) => setUrlForm({ ...urlForm, url: detail.value })}
                placeholder="https://…"
                type="url"
                disabled={savingUrl}
              />
            </FormField>
          </SpaceBetween>
        </div>
      </div>
    );

  return (
    <div className="ivt-kb">
      {/* ── Header: title + source count. Upload lives on the "Sources" row (→ root);
           a selected folder gets its own Upload/Add URL in the right pane (→ that folder).
           So the upload TARGET is decided by which button you click — no implicit scope. ── */}
      <div className="ivt-kb__header">
        <span className="ivt-kb__title">
          <Icon name="gen-ai" /> Knowledge Base
        </span>
        <span className="ivt-kb__count">{entryCount} sources</span>
        {/* One hidden file input, shared by every Upload button. The click handler records
            the intended target folder in uploadTargetRef just before opening the picker. */}
        <input
          ref={inputRef}
          type="file"
          accept={KB_UPLOAD_ACCEPT}
          multiple
          style={{ display: 'none' }}
          onChange={(e) => void handleSelected(e.target.files)}
          data-testid="kb-file-input"
        />
      </div>

      <Alert type="info">
        文件上传后需要一段时间建立知识库（时长与文件大小、数量有关）；点击刷新按钮可查看下方文件的最新处理进度，「已就绪」后即可用于 AI 问答。
      </Alert>

      {uploadError && (
        <Alert type="error" dismissible onDismiss={() => void reload()}>
          <span style={{ whiteSpace: 'pre-line' }}>{uploadError}</span>
        </Alert>
      )}

      <div className="ivt-kb__body">
        {/* ── LEFT rail: Sources label + refresh + root Upload; then folders + root
             entries. This Upload always targets ROOT (every stage, incl. flat gamma/prod
             where there are no folders — so uploading never depends on a folder). ── */}
        <div className="ivt-kb__rail">
          <div className="ivt-kb__rail-head">
            <span className="ivt-kb__rail-label">Sources</span>
            <span className="ivt-kb__rail-actions">
              <Button
                variant="inline-icon"
                iconName="refresh"
                ariaLabel="刷新文件状态"
                loading={loading}
                onClick={() => void reload()}
              />
              <Button
                variant="inline-link"
                iconName="upload"
                loading={uploading}
                onClick={() => openUpload(undefined)}
              >
                Upload
              </Button>
            </span>
          </div>

          {loading ? (
            <div className="ivt-kb__rail-loading">
              <Spinner /> 加载…
            </div>
          ) : (
            <>
              {/* Overview — pinned at the top, always present (placeholder when unsaved).
                  Same row style as a file. Once saved it ingests into the KB like any
                  doc, so show the SAME status badge (placeholder has no status). Never
                  deletable. */}
              <button
                type="button"
                className={`ivt-kb__entry ivt-kb__entry--overview${isOverviewSelected ? ' ivt-kb__entry--active' : ''}`}
                onClick={selectOverview}
                title="Overview"
              >
                <span className="ivt-kb__entry-name">Overview</span>
                {overviewSaved && (() => {
                  const sv = statusView(overview?.status);
                  return (
                    <span className="ivt-kb__entry-status">
                      <StatusIndicator type={sv.type}>{sv.label}</StatusIndicator>
                    </span>
                  );
                })()}
              </button>
              {/* Folders (collapsible), each with its entry count. Empty until the
                  backend seeds folders — then they appear here with no code change.
                  Two hit regions: the caret toggles expand/collapse; the body selects
                  the folder (= sets it as the Upload target + expands). Not one <button>
                  wrapping another (invalid HTML) — a flex row with two child buttons. */}
              {folders.map(({ folder, entries }) => {
                const fid = folder.folderId as string;
                const open = !collapsed.has(fid);
                const active = selectedFolderId === fid;
                return (
                  <div className="ivt-kb__folder" key={folder.folderId}>
                    <div className={`ivt-kb__folder-head${active ? ' ivt-kb__folder-head--active' : ''}`}>
                      <button
                        type="button"
                        className="ivt-kb__folder-caret-btn"
                        aria-expanded={open}
                        aria-label={open ? `收起 ${folder.name}` : `展开 ${folder.name}`}
                        onClick={() => toggleFolder(fid)}
                      >
                        <span className="ivt-kb__folder-caret">{open ? '▾' : '▸'}</span>
                      </button>
                      <button
                        type="button"
                        className="ivt-kb__folder-body"
                        onClick={() => selectFolder(fid)}
                        title={folder.name}
                      >
                        <Icon name="folder" />
                        <span className="ivt-kb__folder-name">{folder.name}</span>
                        <span className="ivt-kb__folder-count">({folder.entryCount})</span>
                      </button>
                    </div>
                    {open && <div className="ivt-kb__folder-entries">{entries.map(entryRow)}</div>}
                  </div>
                );
              })}
              {/* Root-level entries (no folder). */}
              {rootEntries.map(entryRow)}
            </>
          )}
        </div>

        {/* ── RIGHT pane: preview / download placeholder ── */}
        <div className="ivt-kb__content">
          {error ? (
            <Alert type="error" header="无法加载知识库文件" action={<Button onClick={reload}>重试</Button>}>
              {error}
            </Alert>
          ) : overviewPlaceholderSelected ? (
            // Overview never saved → placeholder view: prompt to add (no template).
            <div className="ivt-kb__file-view">
              <div className="ivt-kb__file-head">
                <span className="ivt-kb__file-name">Overview</span>
                <span className="ivt-kb__file-actions">
                  {editing ? (
                    <>
                      <Button variant="primary" loading={savingOverview} onClick={() => void handleSaveEdit()}>
                        保存
                      </Button>
                      <Button variant="link" disabled={savingOverview} onClick={() => setDraft(null)}>
                        取消
                      </Button>
                    </>
                  ) : (
                    // Icon-only pencil — same per-entry Edit as a file (no disabled gate;
                    // the placeholder has no preview to wait on).
                    <Button
                      variant="icon"
                      iconName="edit"
                      ariaLabel="编辑 Overview"
                      onClick={startEdit}
                    />
                  )}
                </span>
              </div>
              {overviewError && <Alert type="error">{overviewError}</Alert>}
              {editing ? (
                <div className="ivt-kb__editor">
                  <Textarea
                    value={draft ?? ''}
                    onChange={({ detail }) => setDraft(detail.value)}
                    rows={20}
                    disabled={savingOverview}
                    ariaLabel="编辑 Overview 内容"
                    placeholder="用 markdown 写这个 Asset 的概述…"
                    spellcheck={false}
                  />
                </div>
              ) : (
                // Centered empty placeholder, same treatment as the binary "no preview"
                // state — not cramped in the top-left corner.
                <div className="ivt-kb__overview-empty">
                  <Icon name="file" size="big" />
                  <div className="ivt-kb__overview-hint">还没有概述。点击 Edit 添加（支持 markdown）。</div>
                </div>
              )}
            </div>
          ) : selectedFolder && urlForm?.mode === 'add' ? (
            // Add URL INTO this folder — the inline form replaces the folder prompt.
            urlFormView('Add URL')
          ) : selectedFolder ? (
            // A folder is selected → its own view: a head (folder name + Upload / Add URL
            // scoped to THIS folder) over a centered upload prompt. The folder's files are
            // NOT re-listed here (they're nested in the left rail — no duplication); the
            // pane's job is the folder-scoped upload + add-URL entry point.
            <div className="ivt-kb__file-view">
              <div className="ivt-kb__file-head">
                <span className="ivt-kb__file-name">
                  <Icon name="folder" /> {selectedFolder.folder.name}
                </span>
                <span className="ivt-kb__file-size">{selectedFolder.entries.length} 个文件</span>
                <span className="ivt-kb__file-actions">
                  {/* Add a URL entry INTO this folder — opens the inline form (below). */}
                  <Button
                    iconName="add-plus"
                    onClick={() => {
                      clearUrlError();
                      setUrlForm({ mode: 'add', folderId: selectedFolder.folder.folderId as string, displayName: '', url: '' });
                    }}
                  >
                    Add URL
                  </Button>
                  {/* Upload INTO this folder (target = its folderId, not root). */}
                  <Button
                    variant="primary"
                    iconName="upload"
                    loading={uploading}
                    onClick={() => openUpload(selectedFolder.folder.folderId as string)}
                  >
                    Upload
                  </Button>
                </span>
              </div>
              {/* Centered prompt (mirrors the Overview placeholder treatment) — the upload
                  target is this folder; files are viewed via the left rail. */}
              <div className="ivt-kb__overview-empty">
                <Icon name="folder" size="big" />
                <div className="ivt-kb__overview-hint">
                  点右上角 Upload 上传到「{selectedFolder.folder.name}」，或 Add URL 添加链接。
                </div>
                <div className="ivt-kb__overview-hint">展开左栏可查看其中的文件。</div>
              </div>
            </div>
          ) : !selectedEntry ? (
            <div className="ivt-kb__empty">
              {entryCount === 0 ? (
                <div className="ivt-kb__empty-hint">
                  还没有知识库文件。点击左侧 Upload 上传 pdf / doc / docx / pptx / xls / xlsx / csv / html / txt / md（单文件 ≤ 500MB），或选中文件夹后 Add URL 添加链接。
                </div>
              ) : (
                <div className="ivt-kb__empty-hint">从左侧选择一个文件查看。</div>
              )}
            </div>
          ) : isUrlEntry(selectedEntry) ? (
            // Selected URL entry. Edit mode → the inline form; else the read view:
            // header (name + Edit/Delete, no Download — a url has no bytes) + body
            // (Display Name + a clickable link, gated to http(s), rel=noopener).
            urlForm?.mode === 'edit' ? (
              urlFormView('Edit URL')
            ) : (
              <div className="ivt-kb__file-view">
                <div className="ivt-kb__file-head">
                  <span className="ivt-kb__file-name">{selectedEntry.displayName ?? '—'}</span>
                  <span className="ivt-kb__file-actions">
                    <Button
                      variant="icon"
                      iconName="edit"
                      ariaLabel={`编辑 ${selectedEntry.displayName ?? ''}`}
                      onClick={() => {
                        clearUrlError();
                        setUrlForm({
                          mode: 'edit',
                          fileId: selectedEntry.fileId as string,
                          displayName: selectedEntry.displayName ?? '',
                          url: selectedEntry.url ?? '',
                        });
                      }}
                    />
                    <Button
                      variant="icon"
                      iconName="remove"
                      ariaLabel={`删除 ${selectedEntry.displayName ?? ''}`}
                      loading={deletingId === selectedEntry.fileId}
                      onClick={() => setConfirmDelete(selectedEntry)}
                    />
                  </span>
                </div>
                <div className="ivt-kb__url-view">
                  <div className="ivt-kb__url-field-label">Display Name</div>
                  <div className="ivt-kb__url-field-value">{selectedEntry.displayName ?? '—'}</div>
                  <div className="ivt-kb__url-field-label">URL</div>
                  <div className="ivt-kb__url-field-value">
                    {/* Render as a clickable link ONLY for http(s) (second guard beyond the
                        backend); otherwise plain text. displayName/url are inserted as DATA
                        (JSX interpolation auto-escapes) — never string-concatenated into HTML. */}
                    {isHttpUrl(selectedEntry.url) ? (
                      <a href={selectedEntry.url} target="_blank" rel="noopener noreferrer">
                        {selectedEntry.url}
                      </a>
                    ) : (
                      <span>{selectedEntry.url ?? '—'}</span>
                    )}
                  </div>
                </div>
              </div>
            )
          ) : (
            <div className="ivt-kb__file-view">
              <div className="ivt-kb__file-head">
                {/* Overview shows its fixed label; a file shows its name + size. */}
                <span className="ivt-kb__file-name">{isOverviewSelected ? 'Overview' : selectedEntry.displayName ?? '—'}</span>
                {!isOverviewSelected && (
                  <span className="ivt-kb__file-size">{formatSize(selectedEntry.fileSizeBytes)}</span>
                )}
                <span className="ivt-kb__file-actions">
                  {editing ? (
                    <>
                      <Button
                        variant="primary"
                        loading={isOverviewSelected ? savingOverview : savingEditId === selectedEntry.fileId}
                        onClick={() => void handleSaveEdit()}
                      >
                        保存
                      </Button>
                      <Button
                        variant="link"
                        disabled={isOverviewSelected ? savingOverview : savingEditId === selectedEntry.fileId}
                        onClick={() => setDraft(null)}
                      >
                        取消
                      </Button>
                    </>
                  ) : (
                    <>
                      {/* Per-entry Edit (pencil) — for Overview or a txt/md file. Gated on
                          the preview having loaded so the editor never opens on empty/stale
                          text. Sits with Download/Delete: all three act on the selection. */}
                      {canEditSelected && (
                        <Button
                          variant="icon"
                          iconName="edit"
                          ariaLabel={`编辑 ${isOverviewSelected ? 'Overview' : selectedEntry.displayName ?? ''}`}
                          disabled={preview.loading || preview.error !== null}
                          onClick={startEdit}
                        />
                      )}
                      {/* Download works for a saved Overview too — it's a real markdown file
                          in the KB. (This branch is only reached for a SAVED overview; the
                          unsaved placeholder is handled earlier.) */}
                      <Button
                        variant="icon"
                        iconName="download"
                        ariaLabel={`下载 ${isOverviewSelected ? 'Overview' : selectedEntry.displayName ?? ''}`}
                        loading={downloadingId === selectedEntry.fileId}
                        onClick={() => void download(selectedEntry)}
                      />
                      {/* Delete stays file-only — Overview is undeletable (sourceType=
                          'OVERVIEW'; backend 409s a delete anyway). */}
                      {!isOverviewSelected && (
                        <Button
                          variant="icon"
                          iconName="remove"
                          ariaLabel={`删除 ${selectedEntry.displayName ?? ''}`}
                          loading={deletingId === selectedEntry.fileId}
                          onClick={() => setConfirmDelete(selectedEntry)}
                        />
                      )}
                    </>
                  )}
                </span>
              </div>
              {isOverviewSelected && overviewError && <Alert type="error">{overviewError}</Alert>}
              {downloadError && <Alert type="error">{downloadError}</Alert>}
              {!isOverviewSelected && editError && <Alert type="error">{editError}</Alert>}
              {editing ? (
                <div className="ivt-kb__editor">
                  <Textarea
                    value={draft ?? ''}
                    onChange={({ detail }) => setDraft(detail.value)}
                    rows={20}
                    disabled={isOverviewSelected ? savingOverview : savingEditId === selectedEntry.fileId}
                    ariaLabel={`编辑 ${isOverviewSelected ? 'Overview' : selectedEntry.displayName ?? ''} 内容`}
                    spellcheck={false}
                  />
                </div>
              ) : isTextFile(selectedEntry.displayName) || isOverviewSelected ? (
                preview.loading ? (
                  <div className="ivt-kb__preview-todo">
                    <Spinner /> 加载预览…
                  </div>
                ) : preview.error ? (
                  isOverviewSelected ? (
                    <Alert type="error">{preview.error}</Alert>
                  ) : (
                    <Alert type="error" action={<Button onClick={() => void download(selectedEntry)}>下载</Button>}>
                      {preview.error}
                    </Alert>
                  )
                ) : isOverviewSelected || (selectedEntry.displayName ?? '').toLowerCase().endsWith('.md') ? (
                  <div className="ivt-kb__preview md-content">
                    <MarkdownRenderer markdown={preview.text ?? ''} />
                  </div>
                ) : (
                  <pre className="ivt-kb__preview-text">{preview.text ?? ''}</pre>
                )
              ) : (
                <div className="ivt-kb__binary">
                  <div className="ivt-kb__binary-icon">
                    <Icon name="file" size="big" />
                  </div>
                  <div className="ivt-kb__binary-name">{selectedEntry.displayName ?? '—'}</div>
                  <div className="ivt-kb__binary-hint">此格式暂不支持预览，点右上角下载查看。</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation — destructive, so require an explicit confirm. */}
      <Modal
        visible={confirmDelete !== null}
        onDismiss={() => setConfirmDelete(null)}
        header="删除知识库文件"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setConfirmDelete(null)}>
                取消
              </Button>
              <Button
                variant="primary"
                loading={deletingId != null}
                onClick={() => void handleConfirmDelete()}
              >
                删除
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {deleteError && (
          <Alert type="error" dismissible onDismiss={() => reload()}>
            {deleteError}
          </Alert>
        )}
        确定删除「{confirmDelete?.displayName}」吗？此操作不可恢复。
      </Modal>
    </div>
  );
}

/**
 * AssetKbFiles — the Asset detail KB tab. Thin wrapper: call useKb with ASSET owner,
 * render the owner-agnostic KbFilesPanel. (UC detail renders KbFilesPanel with
 * useKb('UC', …) — same panel, different owner.)
 */
export default function AssetKbFiles({ assetId }: { assetId: string }) {
  const kb = useKb('ASSET', assetId);
  return <KbFilesPanel kb={kb} />;
}
